import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Checkbox, FormControlLabel, FormGroup, FormHelperText, Grid, TextField } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { getMaxCycles, getMaxLength } from "../functions";
import RepeatPattern from "./RepeatPattern";
import RepeatCycle from "./RepeatCycle";
import { DaysOfWeek } from "../../../../constants";

const calculateSchedule = ({ repeatType, timeStart, timeEnd, dayOfWeek, ...params }) => {
    switch (repeatType) {
        case "weekly":
            return {
                repeatType,
                numberOfWeeks: params.length,
                week: (params.pattern || []).filter((p) => p <= params.length),
                occurrences: params.cycles,
                dayOfWeek,
                start: timeStart.format("HH:mm"),
                end: timeEnd.format("HH:mm"),
            };

        case "monthly":
            return {
                repeatType,
                numberOfMonths: params.length,
                months: (params.pattern || []).filter((p) => p <= params.length),
                occurrences: params.cycles,
                dayOfWeek,
                start: timeStart.format("HH:mm"),
                end: timeEnd.format("HH:mm"),
            };

        case "never":
            return {
                repeatType,
                dayOfWeek,
                start: timeStart.format("HH:mm"),
                end: timeEnd.format("HH:mm"),
            };

        default:
            return null;
    }
};

function ActivityTimetabled({ schedule, setSchedule }) {
    const [timeStart, setTimeStart] = useState(dayjs(new Date(!!schedule?.start ? `2000-01-01T${schedule.start}` : null)));
    const [timeEnd, setTimeEnd] = useState(dayjs(new Date(!!schedule?.end ? `2000-01-01T${schedule.end}` : null)));
    const [repeatType, setRepeatType] = useState(schedule?.repeatType || "weekly");
    const [length, setLength] = useState(schedule?.numberOfWeeks || 1);
    const [cycles, setCycles] = useState(schedule?.occurrences || null);
    const [pattern, setPattern] = useState([]);
    const [dayOfWeek, setDayOfWeek] = useState(!!schedule?.dayOfWeek ? schedule.dayOfWeek.map((dow) => Number(dow)) : []);

    const handleRepeatTypeChange = useCallback(
        (event) => {
            if (repeatType !== event.target.value) {
                const maxLength = getMaxLength(event.target.value);
                if (length > maxLength) {
                    setLength(null);
                    setCycles(null);
                } else if (cycles > getMaxCycles(length, maxLength)) {
                    setCycles(null);
                }
                setPattern([]); // clear repeat pattern when changing repeat type
                setRepeatType(event.target.value);
            }
        },
        [cycles, length, repeatType]
    );

    const handlePatternChange = useCallback((newPattern) => { setPattern(newPattern.sort((a, b) => a - b)); }, [setPattern]);
    const handleSetTimeStart = useCallback((time) => setTimeStart(time), []);
    const handleSetTimeEnd = useCallback((time) => setTimeEnd(time), []);

    const handleSetDayOfWeek = ({ value, checked }) => {
        value = Number(value);
        if (!checked) {
            setDayOfWeek(dayOfWeek.filter((day) => day !== value));
        } else if (!dayOfWeek.includes(value)) {
            setDayOfWeek([...dayOfWeek, value]);
        }
    };

    useEffect(() => {
        setSchedule(calculateSchedule({repeatType, timeStart, timeEnd, length, cycles, pattern, dayOfWeek}));
    }, [setSchedule, repeatType, timeStart, timeEnd, length, cycles, pattern, dayOfWeek]);

    return (
        <React.Fragment>
            <Grid container spacing={3} sx={{ margin: "16px 0 16px -24px" }}>
                <Grid item xs={12} sm={6} md={6}>
                    <TimePicker
                        label="From *"
                        value={dayjs(timeStart)}
                        onChange={handleSetTimeStart}
                        renderInput={(params) => <TextField {...params} />}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={6}>
                    <TimePicker
                        label="To *"
                        value={dayjs(timeEnd)}
                        onChange={handleSetTimeEnd}
                        renderInput={(params) => <TextField {...params} />}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={12}>
                    <FormGroup aria-label="position" row>
                        {DaysOfWeek.map((option) => (
                            <FormControlLabel
                                key={option.value}
                                control={
                                    <Checkbox
                                        value={option.value}
                                        onChange={({target}) => handleSetDayOfWeek(target)}
                                        defaultChecked={dayOfWeek.includes(option.value)}
                                    />
                                }
                                label={option.name}
                            />
                        ))}
                    </FormGroup>
                    <FormHelperText>
                        day of week <span className="error"> *</span>
                    </FormHelperText>
                </Grid>
            </Grid>
            <Grid container spacing={3} sx={{ margin: "16px 0 16px -24px" }}>
                <Grid item xs={4} sm={4} md={4}>
                    <TextField
                        variant="outlined"
                        id="select-repeat"
                        select
                        label="Repeats"
                        helperText="when this activity occurs"
                        value={repeatType}
                        onChange={handleRepeatTypeChange}
                    >
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="never">Does not repeat</MenuItem>
                    </TextField>
                </Grid>
                <RepeatCycle
                    type={repeatType}
                    length={length}
                    setLength={setLength}
                    cycles={cycles}
                    setCycles={setCycles}
                />
            </Grid>

            <Grid container spacing={3} sx={{ margin: "16px 0 16px -24px" }}>
                <RepeatPattern
                    pattern={pattern}
                    setPattern={handlePatternChange}
                    maxCycles={length}
                />
            </Grid>
        </React.Fragment>
    );
}

ActivityTimetabled.propTypes = {
    schedule: PropTypes.oneOfType([
        // no repeat:
        PropTypes.shape({
            repeatType: PropTypes.oneOf(["never"]),
            start: PropTypes.string,
            end: PropTypes.string,
        }),
        // weekly repeat:
        PropTypes.shape({
            repeatType: PropTypes.oneOf(["weekly"]),
            numberOfWeeks: PropTypes.number,
            week: PropTypes.arrayOf(PropTypes.number),
            occurrences: PropTypes.number,

            start: PropTypes.string,
            end: PropTypes.string,
        }),
        // monthly repeat:
        PropTypes.shape({
            repeatType: PropTypes.oneOf(["monthly"]),
            numberOfMonths: PropTypes.number,
            month: PropTypes.arrayOf(PropTypes.number),
            occurrences: PropTypes.number,

            start: PropTypes.string,
            end: PropTypes.string,
        }),
    ]),
    setSchedule: PropTypes.func.isRequired,
};
export default ActivityTimetabled;
